import React, {
  ChangeEvent,
  FC,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Autocomplete, debounce, TextField } from '@mui/material';
import { FieldMetaState, FieldRenderProps } from 'react-final-form';
import { DadataSuggestion, suggestAddress } from '../../../api/dadata';
import { DadataAddressResponse } from '../../../structures/response/dadata/DadataAddressResponse';

interface Props extends Partial<FieldRenderProps<string>> {
  onChange: (v: string) => void;
  onItemSelected: (data: DadataAddressResponse) => void;
  meta: FieldMetaState<string>;
  value: string;
  onDadataFailed?: () => void;
}

const AddressAutosuggest: FC<Props> = ({
  onChange,
  value,
  meta,
  onBlur,
  label,
  onItemSelected,
  onDadataFailed,
}) => {
  const [text, setText] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<
    DadataSuggestion<DadataAddressResponse>[]
  >([]);

  const fetchSuggestions = async (v: string) => {
    try {
      const response = await suggestAddress(v);
      if (Array.isArray(response?.suggestions)) {
        setSuggestions(response.suggestions);
      }
    } catch (e) {
      if (typeof onDadataFailed === 'function') {
        onDadataFailed();
      }
      console.log(e);
    }
  };

  const fetchSuggestionsThrottled = useMemo(
    () => debounce(fetchSuggestions, 200),
    []
  );

  useEffect(() => {
    fetchSuggestionsThrottled(inputValue);
  }, [inputValue]);

  useEffect(() => {
    setInputValue(value);
    setText(value);
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e?.target?.value);
  };

  const handleItemSelect = (
    e: SyntheticEvent,
    data: DadataSuggestion<DadataAddressResponse> | null | string
  ) => {
    if (typeof data === 'string') {
      onChange(data);
    } else {
      onChange(data?.value || '');
      if (data?.data) {
        onItemSelected(data.data);
      }
    }
  };

  return (
    <Autocomplete
      freeSolo
      autoComplete
      selectOnFocus
      inputValue={text}
      options={suggestions}
      onChange={handleItemSelect}
      filterOptions={(x) => x}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.value
      }
      renderInput={(params) => (
        <TextField
          {...params}
          error={meta.touched && meta.invalid}
          helperText={meta?.touched ? meta?.error : ''}
          onChange={handleChange}
          label={label || 'БИК'}
        />
      )}
    />
  );
};

export default AddressAutosuggest;
